import type * as React from 'react';

export const KeyInfoModuleItemName = {
  CourseCount: 'COURSE_COUNT',
  Difficulty: 'DIFFICULTY',
  Duration: 'DURATION',
  EnterpriseCredit: 'ENTERPRISE_CREDIT',
  Item_1: 'ITEM_1',
  Item_2: 'ITEM_2',
  Item_3: 'ITEM_3',
  Item_4: 'ITEM_4',
  Reviews: 'REVIEWS',
  Schedule: 'SCHEDULE',
  Stackability: 'STACKABILITY',
} as const;

export const AboutModuleSubsectionName = {
  DetailsToKnow: 'DETAILS_TO_KNOW',
  LearningOutcomes: 'LEARNING_OUTCOMES',
  Skills: 'SKILLS',
} as const;

export const ProductDetailItemNames = {
  ShareableCertificate: 'ShareableCertificate',
  Languages: 'Languages',
  IndustryCertification: 'IndustryCertification',
  RecentlyUpdated: 'RecentlyUpdated',
  ShareableCaseStudy: 'ShareableCaseStudy',
  Exclusive: 'Exclusive',
  SyllabusContent: 'SyllabusContent',
  LanguagesWithMLInfo: 'LanguagesWithMLInfo',
  InstallationRequirements: 'InstallationRequirements',
  Stackability: 'Stackability',
  ItemCounts: 'ItemCounts',
} as const;

export const UpsellCardNames = {
  CourseraPlusUpsellCard: 'CourseraPlusUpsellCard',
  DegreeUpsellCard: 'DegreeUpsellCard',
  EnterpriseUpsellCard: 'EnterpriseUpsellCard',
} as const;

const courseCountKey: keyof typeof KeyInfoModuleItemName = 'CourseCount';
export const KeyInformationItemNames = {
  ...KeyInfoModuleItemName,
  /** @deprecated Content count is now the intended naming convention. CourseCount is maintained for Contentful integration.  */
  [courseCountKey]: KeyInfoModuleItemName.CourseCount,
  /** Content Count is the replacement for Course Count and uses the existing 'COURSE_COUNT' value to support Contentful. */
  ContentCount: 'COURSE_COUNT',
  InstallationRequirements: 'InstallationRequirements',
  LearningMethod: 'LearningMethod',
  CourseSatisfactionScore: 'CourseSatisfactionScore',
  ProductType: 'ProductType',
  EarnCertificate: 'EarnCertificate',
} as const;

export type KeyInformationItemConfig = {
  name: (typeof KeyInformationItemNames)[keyof typeof KeyInformationItemNames];
  title?: string | null;
  subtitle?: string | null;
};

export type KeyInfoModule = {
  items?: KeyInformationItemConfig[];
};

export type ProductOverviewModuleConfig = {
  name: string;
  heroModule?: { shortDescription?: string | null } & {
    backgroundColor?: string;
  };
  aboutModule?: { subsections?: Array<{ name: string }> };
  keyInfoModule?: {
    items?: Array<{
      name: string;
      title?: string | null;
      subtitle?: string | null;
    }>;
  } & KeyInfoModule;
  removeLinksAboveFold?: boolean;
  showHeroLanguageInfo?: boolean;
  showAssignButton?: boolean;
  hideCourseraPlus?: boolean;
  hideInstructors?: boolean;
  showGoogleBackgroundImage?: boolean;
  productDetailItems: Array<(typeof ProductDetailItemNames)[keyof typeof ProductDetailItemNames]>;
  backgroundImage?: string;
  backgroundImageElement?: React.ReactNode;
};

export const ModuleNames = {
  ArticlesRecommendations: 'ARTICLES_RECOMMENDATIONS',
  Breadcrumbs: 'BREADCRUMBS',
  CareerCertificate: 'CAREER_CERTIFICATE',
  CareerOutcomes: 'CAREER_OUTCOMES',
  CrossSell: 'CROSS_SELL',
  EligibleDegrees: 'ELIGIBLE_DEGREES',
  Faqs: 'FAQS',
  LearnerIncentive: 'LEARNER_INCENTIVE',
  PageBanner: 'PAGE_BANNER',
  ProductOverview: 'PRODUCT_OVERVIEW',
  Syllabus: 'SYLLABUS',
  Testimonials: 'TESTIMONIALS',
  ThinBanner: 'THIN_BANNER',
  Upsell: 'UPSELL',
  ProjectInstructions: 'ProjectInstructions',
  Reviews: 'Reviews',
  Recommendations: 'Recommendations',
  Benefits: 'Benefits',
  RegionalPromotionBanner: 'RegionalPromotionBanner',
  AiDisclaimer: 'AiDisclaimer',
  ComparisonTable: 'ComparisonTable',
  RelatedSkills: 'RelatedSkills',
  RecommendationCollection: 'RecommendationCollection',
} as const;

export type ModuleName = (typeof ModuleNames)[keyof typeof ModuleNames];

export type PageConfig = {
  modules: Array<{ name: (typeof ModuleNames)[keyof typeof ModuleNames] }>;
};
